import React, { forwardRef, ReactElement } from 'react'
import {
  Select as MSelect,
  SelectProps,
  Box,
  InputLabel,
  Typography,
  MenuItem,
  FormHelperText,
  menuItemClasses,
  outlinedInputClasses,
} from '@mui/material'
import { styled } from '@mui/material/styles'
import { ArrowDown } from '@/components/icons'

const ArrowDownWrapper = styled('div')`
  position: absolute;
  right: 12px;
  top: 9px;
  width: 24px;
  height: 24px;
  cursor: pointer;
  user-select: none;
  pointer-events: none;
`

const ArrowDawnButton = () => (
  <ArrowDownWrapper>
    <ArrowDown />
  </ArrowDownWrapper>
)

const StyledSelect = styled(MSelect)(({ theme }) => ({
  [`.${outlinedInputClasses.notchedOutline}`]: {
    border: `2px solid ${theme.custom.colors.common.grey1}`,
  },
  [`.${outlinedInputClasses.input}`]: {
    padding: '11px 32px 12px 13px',
    backgroundColor: theme.palette.common.white,
  },
  [`.${outlinedInputClasses.root}:hover .${outlinedInputClasses.notchedOutline}`]: {
    border: `2px solid ${theme.custom.colors.common.green}`,
  },
  [`&.${outlinedInputClasses.root}:hover .${outlinedInputClasses.notchedOutline}, &.${outlinedInputClasses.focused} .${outlinedInputClasses.notchedOutline}`]:
    {
      border: `2px solid ${theme.custom.colors.common.green}`,
    },
}))

const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
  [`.${menuItemClasses.root}.${menuItemClasses.selected}, .${menuItemClasses.root}.${menuItemClasses.selected}:hover`]:
    {
      backgroundColor: theme.custom.colors.common.green1,
    },
}))

const defaultIterator = () => (item: string) => (
  <StyledMenuItem key={item} value={item}>
    <Typography variant="body1">{item}</Typography>
  </StyledMenuItem>
)

interface KermitSelectBase<T> extends Omit<SelectProps<T>, 'value'> {
  list: T
  helperText?: string
}

interface KermitSelectAdvanced<T> extends KermitSelectBase<T> {
  labelProperty: string
  valueProperty: string
  iterator: (labelProperty: string, valueProperty: string) => (item: T, index: number, list: T[]) => ReactElement
}

type KermitSelect<T> = KermitSelectBase<T> | KermitSelectAdvanced<T>

export const Select: React.ForwardRefExoticComponent<
  React.PropsWithoutRef<KermitSelect<any>> & React.RefAttributes<unknown>
> = forwardRef((props, ref) => {
  const {
    label,
    error,
    list,
    iterator = defaultIterator,
    labelProperty,
    valueProperty,
    helperText,
    ...rest
  } = props as SelectProps & KermitSelectAdvanced<any>
  return (
    <Box>
      <InputLabel error={error}>
        <Box sx={{ mb: '6px' }}>
          <Typography variant="caption">{label}</Typography>
        </Box>
        <StyledSelect IconComponent={ArrowDawnButton} variant="outlined" {...rest} ref={ref} error={error}>
          {list.map(iterator(labelProperty, valueProperty))}
        </StyledSelect>
        {error && helperText && <FormHelperText error>{helperText}</FormHelperText>}
      </InputLabel>
    </Box>
  )
})
